package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddClickable

// ──────────────────────────────────────────────────────────────────────
// Chercher un mot dans une compétence : son intention, son parcours (étapes,
// objectifs, tâches, projets, actions), les notes de ses courses et ses cartes.
// Chaque occurrence est montrée avec d'où elle vient et le mot en évidence.
// ──────────────────────────────────────────────────────────────────────

/**
 * L'endroit de l'écran qu'un résultat ouvre : la clé de la ligne à montrer, et ce qu'il
 * faut déplier pour y arriver (l'étape, l'objectif). Null : le haut de la compétence.
 */
internal data class SkillTarget(val key: String, val stepId: String? = null, val objectiveId: String? = null)

/** Un texte où chercher : d'où il vient, son titre, le texte lui-même, et où il mène. */
internal data class SkillText(val where: String, val title: String, val text: String, val target: SkillTarget? = null)

/** Une occurrence : la source, un extrait autour du mot, la place du mot dans l'extrait, et où elle mène. */
internal data class SkillHit(val where: String, val title: String, val snippet: String, val ranges: List<IntRange>, val target: SkillTarget? = null)

/** Nombre de caractères gardés de part et d'autre du mot dans un extrait. */
private const val AROUND = 60

/**
 * Les occurrences de [query] dans [texts], sans tenir compte de la casse ni des
 * accents. Un texte qui contient le mot plusieurs fois donne un seul extrait, celui
 * de la première, avec toutes les places du mot qui y tombent.
 */
internal fun findInSkill(texts: List<SkillText>, query: String): List<SkillHit> {
    val needle = fold(query.trim())
    if (needle.length < 2) return emptyList()
    return texts.mapNotNull { source ->
        val hay = fold(source.text)
        // Replier ne change pas la longueur : les positions valent dans le texte d'origine.
        val starts = generateSequence(hay.indexOf(needle)) { at -> hay.indexOf(needle, at + 1).takeIf { it >= 0 } }
            .takeWhile { it >= 0 }.toList()
        if (starts.isEmpty()) return@mapNotNull null
        val from = (starts.first() - AROUND).coerceAtLeast(0)
        val to = (starts.first() + needle.length + AROUND).coerceAtMost(source.text.length)
        val prefix = if (from > 0) "…" else ""
        val suffix = if (to < source.text.length) "…" else ""
        val snippet = prefix + source.text.substring(from, to).replace('\n', ' ') + suffix
        val ranges = starts.filter { it >= from && it + needle.length <= to }
            .map { (it - from + prefix.length) until (it - from + prefix.length + needle.length) }
        SkillHit(source.where, source.title, snippet, ranges, source.target)
    }
}

/** Minuscules sans accents, lettre pour lettre, pour comparer sans changer les positions. */
internal fun fold(text: String): String = buildString(text.length) {
    for (raw in text) append(
        when (val c = raw.lowercaseChar()) {
            'à', 'â', 'ä', 'á', 'ã' -> 'a'
            'ç' -> 'c'
            'é', 'è', 'ê', 'ë' -> 'e'
            'î', 'ï', 'í' -> 'i'
            'ô', 'ö', 'ó', 'õ' -> 'o'
            'ù', 'û', 'ü', 'ú' -> 'u'
            'ÿ' -> 'y'
            'ñ' -> 'n'
            else -> c
        },
    )
}

/** Tout ce qu'une compétence contient de texte, dans l'ordre où l'écran le montre. */
internal fun textsOf(st: SkillStats, model: SkillsModel): List<SkillText> = buildList {
    fun add(where: String, title: String, target: SkillTarget?, vararg parts: String?) {
        val text = parts.filterNotNull().filter { it.isNotBlank() }.joinToString("\n")
        if (text.isNotBlank()) add(SkillText(where, title, text, target))
    }
    add("Intention", st.skill.name, null, st.skill.intention)
    model.parcoursOf(st.skill.id).forEach { step ->
        val sid = step.step.id
        add("Étape", step.step.title, SkillTarget("step:$sid", stepId = sid), step.step.title, step.step.summary, step.step.level_name, step.step.level_desc)
        step.objectives.forEach { obj ->
            add("Objectif", obj.title, SkillTarget("objective:${obj.id}", stepId = sid), obj.title, obj.description, obj.motivation)
            model.tasksOf(obj.id).forEach { t -> add("Tâche", t.title, SkillTarget("task:${t.id}", stepId = sid, objectiveId = obj.id), t.title, t.description, t.deliverable, t.acceptance) }
            // Les tâches rangées sous un jalon de l'objectif : le bloc les montre une fois ouvert.
            model.milestonesOf(obj.id).forEach { ms ->
                model.tasksOf(ms.id).forEach { t -> add("Tâche", t.title, SkillTarget("task:${t.id}", stepId = sid, objectiveId = obj.id), t.title, t.description, t.deliverable, t.acceptance) }
            }
        }
        // Les actions d'une étape projet, posées dans l'étape elle-même.
        step.leaves.filter { it.parent_kind == "project" }.forEach { t -> add("Action", t.title, SkillTarget("task:${t.id}", stepId = sid), t.title, t.description, t.deliverable, t.acceptance) }
    }
    st.objectives.filter { it.step_id == null }.forEach { obj ->
        add("Objectif", obj.title, SkillTarget("objective:${obj.id}"), obj.title, obj.description, obj.motivation)
        model.tasksOf(obj.id).forEach { t -> add("Tâche", t.title, SkillTarget("task:${t.id}", objectiveId = obj.id), t.title, t.description, t.deliverable, t.acceptance) }
    }
    st.projects.forEach { p ->
        add("Projet", p.name, SkillTarget("project:${p.id}"), p.name, p.description, p.objectives)
        model.tasksOf(p.id).filter { it.parent_kind == "project" }.forEach { t -> add("Action", t.title, SkillTarget("task:${t.id}"), t.title, t.description, t.deliverable, t.acceptance) }
    }
    st.explanations.forEach { s -> add("Note de course", formatSessionDay(s.started_at), SkillTarget("session:${s.id}"), s.note) }
    model.mindMapsOf(st.skill.id).forEach { m -> add("Carte heuristique", m.title, SkillTarget("map:${m.id}"), m.title) }
}

private fun formatSessionDay(ms: Long): String = org.teddapp.project.ui.admin.formatDay(ms)

@Composable
internal fun SkillSearchSection(st: SkillStats, model: SkillsModel, compact: Boolean) {
    val query = model.detailSearch
    val hits = remember(st, query) { if (query.trim().length < 2) emptyList() else findInSkill(textsOf(st, model), query) }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), modifier = Modifier.fillMaxWidth()) {
        val shape = RoundedCornerShape(TeddTokens.RSm)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
                .background(TeddTokens.Surface, shape)
                .border(1.dp, TeddTokens.LineStrong, shape)
                .padding(horizontal = TeddSpace.S3),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
        ) {
            TeddIcon(name = "search", size = 14.dp, color = TeddTokens.InkMuted)
            BasicTextField(
                value = query,
                onValueChange = { model.detailSearch = it },
                singleLine = true,
                textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
                cursorBrush = SolidColor(TeddTokens.Ink),
                modifier = Modifier.weight(1f),
                decorationBox = { inner ->
                    if (query.isEmpty()) Text("Chercher un mot dans cette compétence", color = TeddTokens.InkSubtle, style = TeddType.Body)
                    inner()
                },
            )
            if (query.isNotEmpty()) TeddButton(text = "Effacer", onClick = { model.detailSearch = "" }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
        }
        if (query.trim().length >= 2) {
            val total = hits.sumOf { it.ranges.size.coerceAtLeast(1) }
            Text(
                when {
                    hits.isEmpty() -> "Aucune occurrence de « ${query.trim()} »."
                    total == 1 -> "1 occurrence"
                    else -> "$total occurrences dans ${hits.size} endroit${if (hits.size > 1) "s" else ""}"
                },
                style = TeddType.Caption, color = TeddTokens.InkMuted,
            )
            hits.forEach { hit -> HitRow(hit, compact, onOpen = { model.reveal(hit.target) }) }
        }
    }
}

/** Un résultat : cliquer ouvre l'endroit trouvé, le mot toujours en évidence. */
@Composable
private fun HitRow(hit: SkillHit, compact: Boolean, onOpen: () -> Unit) {
    TeddCard(
        modifier = Modifier.fillMaxWidth().teddClickable(onClick = onOpen, shape = RoundedCornerShape(TeddTokens.RMd), hover = TeddTokens.LimeSoft),
        shape = RoundedCornerShape(TeddTokens.RMd),
        padding = androidx.compose.foundation.layout.PaddingValues(horizontal = TeddSpace.S4, vertical = TeddSpace.S3),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalAlignment = Alignment.CenterVertically) {
                Text(hit.where.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(hit.title, style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1, overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                Text(if (hit.target == null) "en haut" else "voir →", style = TeddType.Caption, color = TeddTokens.LimeStrong)
            }
            Text(
                text = buildAnnotatedString {
                    var at = 0
                    hit.ranges.forEach { r ->
                        append(hit.snippet.substring(at, r.first))
                        withStyle(SpanStyle(background = TeddTokens.LimeSoft, fontWeight = FontWeight.SemiBold, color = TeddTokens.Ink)) {
                            append(hit.snippet.substring(r.first, r.last + 1))
                        }
                        at = r.last + 1
                    }
                    append(hit.snippet.substring(at))
                },
                style = TeddType.Body, color = TeddTokens.InkMuted, fontSize = if (compact) 13.sp else 14.sp,
            )
        }
    }
}
